import {Alert} from "antd";
import {IconCircleCheck, IconClock, IconCopy, IconHash} from "@tabler/icons-react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {message} from "@ui";

import BaseModal from "@/components/BaseModal";
import {PrimaryButton, SecondaryButton} from "@/components/Button";
import {useAppConfig} from "@/store/useAppConfig.store";
import useClaimUbiiPagoOrphan, {UbiiPagoOrphanClaimHttpError} from "@/hooks/wallet/useClaimUbiiPagoOrphan";
import {formatCurrencyAmount} from "@/util/balance.util";

import {
  formatUbiiPagoCountdown,
  isValidUbiiPagoAmount,
  isValidUbiiPagoReference,
  normalizeUbiiPagoAmountInput,
  normalizeUbiiPagoReference,
  normalizeUbiiPagoReferenceInput,
  parseUbiiPagoAmount,
  toUbiiPagoAmountCents,
  UBII_PAGO_MAX_ATTEMPTS,
  UBII_PAGO_RATE_LIMIT_COOLDOWN_SECONDS,
} from "@/utils/ubii-pago-orphan-claim.util";

import imgBankPlaza from "@/assets/images/modal-icons/banco-plaza-icon.png";
import imgBankVzla from "@/assets/images/modal-icons/bdv-icon.png";
import imgBancamiga from "@/assets/images/modal-icons/bancamiga-icon.svg";
import styles from "./ubiiPagoOrphanClaimModal.module.css";

const BANCAMIGA_INFO = {
  id: "bancamiga",
  name: "Bancamiga",
  rif: "J-504732001",
  phone: "04127230400",
  img: imgBancamiga,
};

const BDV_BANK_NAME = "Banco de Venezuela";

export default function UbiiPagoOrphanClaimModal({open, onClose}) {
  const {t} = useTranslation();

  const claimHook = useClaimUbiiPagoOrphan();
  const {claimOrphan, isPending, reset} = claimHook;

  const bankName = useAppConfig(state => state.bankName);
  const rif = useAppConfig(state => state.RIF);
  const phone = useAppConfig(state => state.PagoMovilNumber);

  const destinationBanks = useMemo(
    () => [
      {
        id: "bdv",
        name: BDV_BANK_NAME,
        rif: rif ?? "",
        phone: phone ?? "",
        img: imgBankVzla,
      },
      BANCAMIGA_INFO,
      {
        id: "plaza",
        name: bankName,
        rif: rif ?? "",
        phone: phone ?? "",
        img: imgBankPlaza,
      },
    ],
    [bankName, rif, phone]
  );

  const [step, setStep] = useState("banks"); // banks | form | success
  const [selectedBankId, setSelectedBankId] = useState(destinationBanks[0]?.id ?? "bdv");

  const [form, setForm] = useState({amount: "", reference: ""});
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");

  const [success, setSuccess] = useState(null); // {amountCents, newBalanceCents, transactionId}

  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [attemptCount, setAttemptCount] = useState(0);

  const closable = step !== "success";

  const selectedBank = useMemo(
    () => destinationBanks.find(b => b.id === selectedBankId) ?? destinationBanks[0],
    [destinationBanks, selectedBankId]
  );

  const copyPayload = useMemo(() => {
    if (!selectedBank) return "";
    return `${selectedBank.name}\nRIF: ${selectedBank.rif}\n${t("depositInstructionsMobilePhone")}: ${selectedBank.phone}`;
  }, [selectedBank, t]);

  const resetState = useCallback(() => {
    setStep("banks");
    setSelectedBankId(destinationBanks[0]?.id ?? "bdv");
    setForm({amount: "", reference: ""});
    setFieldErrors({});
    setFormError("");
    setSuccess(null);
    setCooldownSeconds(0);
    setAttemptCount(0);
    reset();
  }, [destinationBanks, reset]);

  useEffect(() => {
    if (!open) {
      const timer = window.setTimeout(resetState, 200);
      return () => window.clearTimeout(timer);
    }
  }, [open, resetState]);

  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const timer = window.setInterval(() => {
      setCooldownSeconds(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownSeconds]);

  const validateForm = () => {
    const nextErrors = {};

    const amountValue = parseUbiiPagoAmount(form.amount);
    const reference = normalizeUbiiPagoReference(form.reference);

    if (!isValidUbiiPagoAmount(amountValue)) {
      nextErrors.amount = t("ubiiPagoClaimAmountInvalid");
    }
    if (!isValidUbiiPagoReference(reference)) {
      nextErrors.reference = t("ubiiPagoClaimReferenceInvalid");
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (isPending || cooldownSeconds > 0) return;

    if (!validateForm()) {
      setFormError(t("ubiiPagoClaimErrorInvalid"));
      return;
    }

    setFormError("");

    try {
      const amountValue = parseUbiiPagoAmount(form.amount);
      const claimData = await claimOrphan({
        amount: toUbiiPagoAmountCents(amountValue),
        reference: normalizeUbiiPagoReference(form.reference),
      });

      setAttemptCount(0);
      setSuccess({
        amountCents: claimData.amount,
        newBalanceCents: claimData.new_balance,
        transactionId: claimData.transaction_id,
      });
      setStep("success");
      message.success(t("ubiiPagoClaimSuccessToast"));
    } catch (err) {
      const httpStatus = err instanceof UbiiPagoOrphanClaimHttpError ? err.httpStatus : undefined;
      const messageKey = err instanceof UbiiPagoOrphanClaimHttpError ? err.messageKey : "ubiiPagoClaimErrorGeneric";

      if (httpStatus === 429) {
        setAttemptCount(UBII_PAGO_MAX_ATTEMPTS);
        setCooldownSeconds(UBII_PAGO_RATE_LIMIT_COOLDOWN_SECONDS);
      } else {
        setAttemptCount(prev => Math.min(prev + 1, UBII_PAGO_MAX_ATTEMPTS));
      }

      setFormError(t(messageKey));
    }
  };

  const handleCopy = async () => {
    if (!copyPayload) return;
    try {
      await navigator.clipboard.writeText(copyPayload);
      message.success(t("depositInstructionsCopied"));
    } catch {
      message.error(t("depositInstructionsError"));
    }
  };

  if (!open) return null;

  const title =
    step === "success" ? null : (
      <div className={styles.modalTitle}>
        {step === "form" ? t("ubiiPagoClaimReportTitle") : t("ubiiPagoClaimTitle")}
      </div>
    );

  const submitDisabled = isPending || cooldownSeconds > 0;

  return (
    <BaseModal
      open={open}
      onCancel={onClose}
      width={640}
      centered
      closable={closable}
      title={title}
      footer={null}
    >
      <div className={styles.modalContent}>
        {step === "success" && success && (
          <div className={styles.successCard}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "999px",
                background: "rgba(16, 185, 129, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCircleCheck size={40} style={{color: "rgb(34, 197, 94)"}} />
            </div>

            <h3 style={{margin: 0, fontWeight: 800, color: "var(--text-primary)"}}>{t("ubiiPagoClaimSuccessTitle")}</h3>
            <p style={{margin: 0, color: "var(--text-muted)", textAlign: "center"}}>{t("ubiiPagoClaimSuccessMessage")}</p>

            <div className={styles.successAmountCard}>
              <div className={styles.successAmountRow}>
                <span className={styles.successAmountLabel}>{t("ubiiPagoClaimAmountLabel")}</span>
                <span className={styles.successAmountValue}>{formatCurrencyAmount(success.amountCents, "VES")}</span>
              </div>
              <div className={styles.successAmountRow}>
                <span className={styles.successAmountLabel}>{t("ubiiPagoClaimNewBalanceLabel")}</span>
                <span className={styles.successAmountValue}>{formatCurrencyAmount(success.newBalanceCents, "VES")}</span>
              </div>
            </div>

            <PrimaryButton
              block
              onClick={onClose}
              style={{marginTop: 8}}
            >
              {t("ubiiPagoClaimClose")}
            </PrimaryButton>
          </div>
        )}

        {step === "banks" && (
          <>
            <p className={styles.stepsTitle}>{t("ubiiPagoClaimStepsTitle")}</p>

            <ol className={styles.stepsList}>
              <li className={styles.stepItem}>
                <span className={styles.stepNumber}>1.</span>
                <span>{t("ubiiPagoClaimStep1")}</span>
              </li>
              <li className={styles.stepItem}>
                <span className={styles.stepNumber}>2.</span>
                <span>{t("ubiiPagoClaimStep2")}</span>
              </li>
              <li className={styles.stepItem}>
                <span className={styles.stepNumber}>3.</span>
                <span>{t("ubiiPagoClaimStep3")}</span>
              </li>
              <li className={styles.stepItem}>
                <span className={styles.stepNumber}>4.</span>
                <span>{t("ubiiPagoClaimStep4")}</span>
              </li>
              <li className={styles.stepItem}>
                <span className={styles.stepNumber}>5.</span>
                <span>{t("ubiiPagoClaimStep5")}</span>
              </li>
            </ol>

            <div className={styles.banksGrid}>
              {destinationBanks.map(bank => {
                const isSelected = bank.id === selectedBankId;
                return (
                  <button
                    key={bank.id}
                    type="button"
                    className={`${styles.bankCard} ${isSelected ? styles.bankCardSelected : ""}`}
                    onClick={() => setSelectedBankId(bank.id)}
                    aria-pressed={isSelected}
                  >
                    <img src={bank.img} alt={bank.name} />
                  </button>
                );
              })}
            </div>

            <div className={styles.detailCard}>
              <button
                type="button"
                className={styles.copyBtn}
                aria-label={t("depositInstructionsCopy")}
                onClick={handleCopy}
                title={t("depositInstructionsCopy")}
              >
                <IconCopy size={18} />
              </button>

              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t("depositInstructionsBank")}</span>
                <span className={styles.detailValue}>{selectedBank?.name}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t("depositListRif")}</span>
                <span className={styles.detailValue}>{selectedBank?.rif}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t("depositInstructionsMobilePhone")}</span>
                <span className={styles.detailValue}>{selectedBank?.phone}</span>
              </div>
            </div>

            <div className={styles.footerButtonsFull} style={{justifyContent: "flex-end"}}>
              <SecondaryButton onClick={onClose}>
                {t("ubiiPagoClaimBack")}
              </SecondaryButton>
              <PrimaryButton onClick={() => setStep("form")}>
                {t("ubiiPagoClaimPaidCta")}
              </PrimaryButton>
            </div>
          </>
        )}

        {step === "form" && (
          <>
            <Alert
              message={t("ubiiPagoClaimReportHint")}
              type="info"
              showIcon
              style={{borderRadius: 12}}
            />

            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, marginTop: 6}}>
              <p style={{margin: 0, color: "var(--text-muted)", fontSize: 13}}>
                {t("ubiiPagoClaimAttemptsInfo", {max: UBII_PAGO_MAX_ATTEMPTS})}
              </p>
              <p style={{margin: 0, color: "var(--text-primary)", fontWeight: 800, fontSize: 14}}>
                {t("ubiiPagoClaimAttemptsCount", {current: attemptCount, max: UBII_PAGO_MAX_ATTEMPTS})}
              </p>
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel}>{t("ubiiPagoClaimAmountLabel")}</label>
              <div className={styles.inputWrapper}>
                <span className={styles.inputLeftPrefix}>Bs</span>
                <input
                  className={styles.input}
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={form.amount}
                  onChange={e => {
                    const normalized = normalizeUbiiPagoAmountInput(e.target.value);
                    if (normalized === null) return;
                    setForm(prev => ({...prev, amount: normalized}));
                    setFieldErrors(prev => ({...prev, amount: undefined}));
                    setFormError("");
                  }}
                />
                <span className={styles.inputRightSuffix}>VES</span>
              </div>
              <p className={styles.formHint}>{t("ubiiPagoClaimAmountDecimalHint")}</p>
              {fieldErrors.amount && (
                <Alert type="error" showIcon message={fieldErrors.amount} style={{borderRadius: 12}} />
              )}
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel}>{t("ubiiPagoClaimReferenceLabel")}</label>
              <div className={styles.inputWrapper}>
                <IconHash size={18} className={styles.hashIcon} />
                <input
                  className={`${styles.input} ${styles.inputReference}`}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder={t("ubiiPagoClaimReferencePlaceholder")}
                  value={form.reference}
                  onChange={e => {
                    setForm(prev => ({...prev, reference: normalizeUbiiPagoReferenceInput(e.target.value)}));
                    setFieldErrors(prev => ({...prev, reference: undefined}));
                    setFormError("");
                  }}
                />
              </div>
              <p className={styles.formHint}>{t("ubiiPagoClaimReferenceHint")}</p>
              {fieldErrors.reference && (
                <Alert type="error" showIcon message={fieldErrors.reference} style={{borderRadius: 12}} />
              )}
            </div>

            {formError && (
              <Alert type="error" showIcon message={formError} style={{borderRadius: 12}} />
            )}

            {cooldownSeconds > 0 && (
              <div className={styles.cooldownBox} aria-live="polite">
                <IconClock size={18} style={{color: "var(--text-muted)"}} />
                <div>
                  <div className={styles.cooldownTime}>{formatUbiiPagoCountdown(cooldownSeconds)}</div>
                  <div className={styles.formHint} style={{marginTop: 2}}>
                    {t("ubiiPagoClaimCooldownHint")}
                  </div>
                </div>
              </div>
            )}

            <div className={styles.footerButtons}>
              <SecondaryButton
                onClick={() => setStep("banks")}
                disabled={isPending}
              >
                {t("ubiiPagoClaimBack")}
              </SecondaryButton>
              <PrimaryButton
                onClick={handleSubmit}
                disabled={submitDisabled}
                loading={isPending}
              >
                {t("ubiiPagoClaimSubmit")}
              </PrimaryButton>
            </div>
          </>
        )}
      </div>
    </BaseModal>
  );
}

